/**
 * Firebase Configuration
 * Handles authentication with Google and Apple Sign-In
 */

import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAnalytics, type Analytics } from 'firebase/analytics'
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  type Auth,
  type User as FirebaseUser,
} from 'firebase/auth'
import type { User } from '@/types/auth'

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

// Debug: log Firebase config (without sensitive values)
console.log('[Firebase] Config check:', {
  hasApiKey: !!firebaseConfig.apiKey,
  hasAuthDomain: !!firebaseConfig.authDomain,
  hasProjectId: !!firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain || 'NOT SET',
  projectId: firebaseConfig.projectId || 'NOT SET',
})

// Check if Firebase is configured
export const isFirebaseConfigured = (): boolean => {
  const configured = !!(
    firebaseConfig.apiKey &&
    firebaseConfig.authDomain &&
    firebaseConfig.projectId
  )
  console.log('[Firebase] isFirebaseConfigured:', configured)
  return configured
}

// Initialize Firebase only if configured
let app: FirebaseApp | null = null
let auth: Auth | null = null
let analytics: Analytics | null = null

if (isFirebaseConfigured()) {
  app = initializeApp(firebaseConfig)
  auth = getAuth(app)
  // Initialize Analytics (only in browser environment)
  if (typeof window !== 'undefined' && firebaseConfig.measurementId) {
    analytics = getAnalytics(app)
  }
}

// Auth providers
const googleProvider = new GoogleAuthProvider()
googleProvider.addScope('email')
googleProvider.addScope('profile')

const appleProvider = new OAuthProvider('apple.com')
appleProvider.addScope('email')
appleProvider.addScope('name')

/**
 * Map Firebase user to our User type
 */
function mapFirebaseUser(firebaseUser: FirebaseUser): User {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email || '',
    displayName: firebaseUser.displayName || 'User',
    photoURL: firebaseUser.photoURL,
    createdAt: firebaseUser.metadata.creationTime || new Date().toISOString(),
  }
}

/**
 * Detect if running on any mobile device
 */
function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
}

/**
 * Detect if running in standalone mode (PWA)
 */
function isStandalone(): boolean {
  return window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as Navigator & { standalone?: boolean }).standalone === true
}

// Key for tracking pending redirect login (use localStorage for persistence)
const PENDING_REDIRECT_KEY = 'k-skin-pending-redirect'

/**
 * Mark that we're starting a redirect login flow
 */
export function markPendingRedirect(): void {
  try {
    localStorage.setItem(PENDING_REDIRECT_KEY, Date.now().toString())
    console.log('[Firebase] Marked pending redirect')
  } catch (e) {
    console.error('[Firebase] Failed to set pending redirect:', e)
  }
}

/**
 * Check pending redirect flag (does NOT clear it - let caller decide when to clear)
 * Returns true if redirect was pending and not too old (< 5 minutes)
 */
export function checkAndClearPendingRedirect(): boolean {
  try {
    const pending = localStorage.getItem(PENDING_REDIRECT_KEY)
    if (pending) {
      // Check if the redirect was recent (within 5 minutes)
      const timestamp = parseInt(pending, 10)
      const isRecent = Date.now() - timestamp < 5 * 60 * 1000
      console.log('[Firebase] Found pending redirect, isRecent:', isRecent)
      // Only clear if it's old (stale redirect)
      if (!isRecent) {
        localStorage.removeItem(PENDING_REDIRECT_KEY)
      }
      return isRecent
    }
    return false
  } catch (e) {
    console.error('[Firebase] Failed to check pending redirect:', e)
    return false
  }
}

/**
 * Clear pending redirect flag
 */
export function clearPendingRedirect(): void {
  try {
    localStorage.removeItem(PENDING_REDIRECT_KEY)
    console.log('[Firebase] Cleared pending redirect')
  } catch (e) {
    console.error('[Firebase] Failed to clear pending redirect:', e)
  }
}

/**
 * Detect iOS Safari specifically
 */
function isIOSSafari(): boolean {
  const ua = navigator.userAgent
  const isIOS = /iPad|iPhone|iPod/.test(ua)
  const isWebkit = /WebKit/.test(ua)
  const isNotChrome = !/CriOS/.test(ua)
  return isIOS && isWebkit && isNotChrome
}

/**
 * Sign in with Google
 * On mobile, use redirect flow directly (more reliable)
 * On desktop, try popup first with timeout, fall back to redirect
 */
export async function signInWithGoogle(): Promise<User> {
  if (!auth) {
    throw new Error('Firebase not configured. Please add Firebase credentials to .env')
  }

  const mobile = isMobile()
  const standalone = isStandalone()
  const iosSafari = isIOSSafari()
  console.log('[Firebase] signInWithGoogle called, mobile:', mobile, 'standalone:', standalone, 'iosSafari:', iosSafari)

  // Try popup first on ALL platforms (including mobile)
  // Redirect flow has cross-origin issues when authDomain != app domain
  // Only fall back to redirect if popup is explicitly blocked

  // Desktop: try popup with timeout
  console.log('[Firebase] Desktop: trying popup flow with timeout...')
  try {
    // Add timeout to prevent hanging forever
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Popup timeout - please try again')), 30000)
    })

    console.log('[Firebase] Starting signInWithPopup...')
    const result = await Promise.race([
      signInWithPopup(auth, googleProvider),
      timeoutPromise
    ])
    console.log('[Firebase] Popup succeeded! User:', result.user.email)
    return mapFirebaseUser(result.user)
  } catch (popupError) {
    const errorCode = (popupError as { code?: string })?.code || ''
    const errorMessage = (popupError as { message?: string })?.message || ''
    console.log('[Firebase] Popup failed with code:', errorCode, 'message:', errorMessage)

    // On localhost, don't fall back to redirect - it won't work due to cross-origin issues
    // Just re-throw the error so user can try again
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    if (isLocalhost) {
      console.log('[Firebase] On localhost, not falling back to redirect (cross-origin issues)')
      throw popupError
    }

    // Fall back to redirect for popup issues or timeout (production only)
    if (errorCode === 'auth/popup-blocked' || errorCode === 'auth/cancelled-popup-request' || errorMessage.includes('timeout')) {
      console.log('[Firebase] Popup failed, trying redirect...')
      try {
        markPendingRedirect()
        await signInWithRedirect(auth, googleProvider)
        throw new Error('Redirecting...')
      } catch (redirectError) {
        console.error('[Firebase] Redirect also failed:', redirectError)
        throw popupError // Throw original popup error
      }
    }

    // Re-throw other errors
    throw popupError
  }
}

/**
 * Sign in with Apple
 * On mobile, use redirect flow directly (more reliable)
 * On desktop, try popup first with timeout, fall back to redirect
 */
export async function signInWithApple(): Promise<User> {
  if (!auth) {
    throw new Error('Firebase not configured. Please add Firebase credentials to .env')
  }

  const mobile = isMobile()
  const standalone = isStandalone()
  const iosSafari = isIOSSafari()
  console.log('[Firebase] signInWithApple called, mobile:', mobile, 'standalone:', standalone, 'iosSafari:', iosSafari)

  // Try popup first on ALL platforms (including mobile)
  // Redirect flow has cross-origin issues when authDomain != app domain
  // Only fall back to redirect if popup is explicitly blocked

  // Desktop: try popup with timeout
  console.log('[Firebase] Desktop: trying popup flow for Apple with timeout...')
  try {
    // Add timeout to prevent hanging forever
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error('Popup timeout - please try again')), 30000)
    })

    const result = await Promise.race([
      signInWithPopup(auth, appleProvider),
      timeoutPromise
    ])
    console.log('[Firebase] Apple popup succeeded!')
    return mapFirebaseUser(result.user)
  } catch (popupError) {
    const errorCode = (popupError as { code?: string })?.code || ''
    const errorMessage = (popupError as { message?: string })?.message || ''
    console.log('[Firebase] Apple popup failed with code:', errorCode, 'message:', errorMessage)

    // Fall back to redirect for popup issues or timeout
    if (errorCode === 'auth/popup-blocked' || errorCode === 'auth/cancelled-popup-request' || errorMessage.includes('timeout')) {
      console.log('[Firebase] Popup failed, trying redirect...')
      try {
        markPendingRedirect()
        await signInWithRedirect(auth, appleProvider)
        throw new Error('Redirecting...')
      } catch (redirectError) {
        console.error('[Firebase] Redirect also failed:', redirectError)
        throw popupError // Throw original popup error
      }
    }

    // Re-throw other errors
    throw popupError
  }
}

/**
 * Check for redirect result (called on app init)
 * Returns user if redirected back from OAuth, null otherwise
 */
export async function checkRedirectResult(): Promise<User | null> {
  if (!auth) {
    console.log('[Firebase] checkRedirectResult: auth not initialized')
    return null
  }

  try {
    console.log('[Firebase] checkRedirectResult: checking...')
    const result = await getRedirectResult(auth)
    console.log('[Firebase] checkRedirectResult: result =', result)
    if (result?.user) {
      console.log('[Firebase] checkRedirectResult: got user!', result.user.email)
      return mapFirebaseUser(result.user)
    }
    console.log('[Firebase] checkRedirectResult: no user in result')
    return null
  } catch (error) {
    console.error('[Firebase] Redirect result error:', error)
    return null
  }
}

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  if (!auth) return
  await firebaseSignOut(auth)
}

/**
 * Subscribe to auth state changes
 */
export function onAuthChange(callback: (user: User | null) => void): () => void {
  if (!auth) {
    // Firebase not configured - return no-op unsubscribe
    return () => {}
  }

  return onAuthStateChanged(auth, (firebaseUser) => {
    if (firebaseUser) {
      callback(mapFirebaseUser(firebaseUser))
    } else {
      callback(null)
    }
  })
}

/**
 * Get current user
 */
export function getCurrentUser(): User | null {
  if (!auth?.currentUser) return null
  return mapFirebaseUser(auth.currentUser)
}

/**
 * Wait for Firebase auth to be ready (up to 5 seconds)
 * Firebase needs time to restore auth state from storage on page load
 */
async function waitForAuthReady(): Promise<FirebaseUser | null> {
  if (!auth) return null

  // If already have a current user, return immediately
  if (auth.currentUser) return auth.currentUser

  // Wait for auth state to be restored (with timeout)
  // Note: auth is guaranteed non-null here due to the check above
  const authInstance = auth
  return new Promise((resolve) => {
    const timeout = setTimeout(() => {
      console.log('[Firebase] Auth wait timeout - no user')
      resolve(null)
    }, 5000)

    const unsubscribe = onAuthStateChanged(authInstance, (user) => {
      clearTimeout(timeout)
      unsubscribe()
      resolve(user)
    })
  })
}

/**
 * Get the current user's ID token (JWT) for API authentication
 * Returns null if not authenticated
 */
export async function getIdToken(): Promise<string | null> {
  if (!auth) return null

  // Wait for auth state to be ready
  const user = await waitForAuthReady()
  if (!user) {
    console.log('[Firebase] getIdToken: no user after waiting')
    return null
  }

  try {
    const token = await user.getIdToken()
    console.log('[Firebase] getIdToken: got token')
    return token
  } catch (error) {
    console.error('[Firebase] Failed to get ID token:', error)
    return null
  }
}

export { auth, app, analytics }
